import { Component, inject, OnInit } from '@angular/core';
import { CellRenderer } from '@shared/interfaces/table/cell-renderer';
import { TransferClickService } from '@app/core/services/transfer-click.service';

/**
 * A table cell renderer that displays a value and emits a click event when clicked.
 *
 * This component is used to create interactive cells in a table where clicking
 * the cell triggers an action in the `TransferClickService`, passing a specific
 * area and value associated with the row.
 *
 * Usage in `TableConfig`:
 * ```typescript
 * {
 *   title: 'Interactive Column',
 *   key: 'someKey',
 *   cellRenderer: ClickCellComponent,
 *   cellRendererParams: {
 *     area: 'myArea', // The area identifier for the click event
 *     key: 'id'       // The data key to use as the value for the click event
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-click-cell',
  imports: [],
  templateUrl: './click-cell.component.html',
  styleUrl: './click-cell.component.css'
})
export class ClickCellComponent extends CellRenderer implements OnInit {
  /** Service to emit click events to the rest of the application. */
  private readonly clickService: TransferClickService = inject(TransferClickService);

  /** The area identifier for the click event (e.g., 'meterframe', 'user'). */
  private area?: string;

  /** The specific value to be emitted with the click event (e.g., an ID). */
  private keyValue?: string;

  /**
   * Initializes the component by extracting `area` and `key` from `cellRendererParams`.
   * It resolves the `keyValue` from the row data using the provided `key`.
   */
  public ngOnInit(): void {
    if (this.params?.cellRendererParams?.area) {
      this.area = this.params.cellRendererParams.area;
    }

    if (this.params?.cellRendererParams?.key && this.data) {
      this.keyValue = this.data[this.params.cellRendererParams.key];
    }
  }

  /**
   * Handles the click event on the cell.
   * If both `area` and `keyValue` are resolved, it emits a click event via `TransferClickService`.
   */
  public onClick(): void {
    if (this.area && this.keyValue) {
      this.clickService.emitClick(this.area, this.keyValue);
    }
  }
}
